import React, { useMemo } from 'react';
import cn from 'classnames';
import { format } from 'date-fns';

import { Spinner } from 'components';
import { Avatar } from 'components/Avatar/Avatar.component';
import { Row } from 'components/layout';
import { Button } from 'components/form';
import { ButtonV3 } from 'components/ComponentV2';
import { useCommunityEvents } from 'pages/community/useCommunityEvents';
import { useCommunityBoard } from 'pages/community/useCommunityBoard';
import { PreferencesResponse } from 'lib/models/talent-pool';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import {
  CommunityAttributes,
  CommunityTypes,
  CommunityEventAttributes,
} from 'lib/models/community';

import { TouchpointState } from 'lib/models/opportunity';
import { getOpportunityPublicPagePathname } from 'utils/urls';
import {
  CommunityEventCard,
  CommunityEventCardDataProps,
} from '../EventsCardSection/CommunityEventCard.component';
import styles from './CommunityApplication.module.scss';

const extractStringPreference = (data: string[]): string[] => data || [];
const extractAttributePreference = (
  response: PreferencesResponse | undefined
): string[] => {
  if (!response || !Array.isArray(response.data)) {
    return [];
  }
  return response.data.map((item) => item.attributes.name);
};

interface PreferenceDetailProps {
  label: string;
  values: string[];
}

interface CommunityApplicationProps {
  status: string;
  applicantDetails: CommunityAttributes;
  refetch?: () => void;
}

export const CommunityApplication: React.FC<CommunityApplicationProps> = ({
  status,
  applicantDetails,
  refetch,
}) => {
  const {
    name,
    avatar_url,
    email,
    opportunity_types,
    area_of_responsibilities,
    cities,
    industries,
    candidate_id,
    created_at,
    invited,
    id,
    education_histories,
  } = applicantDetails || {};

  let subscriptionDate = '';
  if (status && status === CommunityTypes.SUBSCRIBED && created_at) {
    subscriptionDate = format(new Date(created_at), 'MMM dd');
  }

  const {
    events,
    size,
    setSize,
    hasMoreRecords,
    isLoading,
    isLoadingMore,
    eventsMetaInfo,
  } = useCommunityEvents(candidate_id);

  const TalentPreferences = useMemo(
    () => ({
      'Looking for': extractStringPreference(opportunity_types),
      'Role preference': extractAttributePreference(area_of_responsibilities),
      'Location preference': parseArrayResponse(cities).map(({ name }) => name),
      'Industry preference': extractAttributePreference(industries),
    }),
    [opportunity_types, area_of_responsibilities, cities, industries]
  );
  const {
    mutateCommunityDashboard,
    onApproveSubscriptionRequests,
    onDeclineSubscriptionRequests,
  } = useCommunityBoard();
  const onAcceptAction = async (ids: string[]) => {
    await onApproveSubscriptionRequests(ids);
    refetch && refetch();
    mutateCommunityDashboard();
  };
  const onDeclineAction = async (ids: string[]) => {
    await onDeclineSubscriptionRequests(ids);
    refetch && refetch();
    mutateCommunityDashboard();
  };

  // Render helper components
  const profileInfo = () => {
    return (
      <div className={styles.candidateProfile}>
        <div className={styles.profileDetails}>
          <Avatar
            className={styles.avatar}
            name={name || email}
            size="xlarge"
            icon={avatar_url || undefined}
          />
          <div>
            <p className={styles.name}>
              {name || (email && email.split('@')[0])}
            </p>
            <div className={styles.details}>
              {status === CommunityTypes.SUBSCRIBED ? (
                <>
                  <span className={cn(styles.status)}>Joined</span>
                  {subscriptionDate ? (
                    <span> since {subscriptionDate} </span>
                  ) : null}
                </>
              ) : (
                <>
                  <span className={styles.detailsText}>{email}</span>
                  {invited ? (
                    <span
                      className={cn(styles.status, {
                        [styles.invited]: true,
                      })}
                    >
                      Invited
                    </span>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const basicInfo = () => {
    return (
      <div className={styles.basicInfo}>
        <p className={styles.title}>Basic info</p>
        <div className={styles.candidateDetails}>
          <div>
            <span className={styles.label}>Email address</span>
            <span>{email}</span>
          </div>
          <div>
            <span className={styles.label}>University</span>
            <span>
              {
                education_histories?.data?.[0]?.attributes?.university?.data
                  ?.attributes?.name
              }
            </span>
          </div>
          {education_histories?.data?.[0]?.attributes?.currently_studying &&
            education_histories?.data?.[0]?.attributes?.current_study_year && (
              <div>
                <span className={styles.label}>Study Year</span>
                <span>
                  {
                    education_histories?.data?.[0]?.attributes
                      ?.current_study_year
                  }
                </span>
              </div>
            )}
          <div>
            <span className={styles.label}>Degree Type</span>
            <span>
              {
                education_histories?.data?.[0]?.attributes?.degree?.data
                  ?.attributes?.name
              }
            </span>
          </div>
          <div>
            <span className={styles.label}>Subject of Study</span>
            <span>
              {
                education_histories?.data[0]?.attributes?.subject?.data
                  ?.attributes?.name
              }
            </span>
          </div>
        </div>
      </div>
    );
  };

  const candidateEvents = () => {
    return (
      <>
        {events?.length ? (
          <div className={styles.events}>
            <p className={styles.title}>Events</p>
            {getCards}
            {!isLoading && isLoadingMore ? (
              <Spinner
                size="small"
                className={styles.spinner}
                wrapperClassname={styles.spinnerWrapper}
                wrapper
              />
            ) : null}
            {eventsMetaInfo?.total ? (
              <div className={styles.tableLayoutFooter}>
                <Row align="center" className={styles.countsRow}>
                  <p>
                    Showing {events?.length} of {eventsMetaInfo?.total}
                  </p>
                  {hasMoreRecords && (
                    <Button
                      variant="text"
                      className={styles.countsButton}
                      onClick={() => setSize(size + 1)}
                    >
                      Show more
                    </Button>
                  )}
                </Row>
              </div>
            ) : null}
          </div>
        ) : null}
      </>
    );
  };
  const footer = () => {
    return (
      <>
        <div className={cn(styles.footerActionBar)}>
          <ButtonV3
            startIcon="icon_check"
            onClick={(e) => {
              e?.preventDefault();
              e?.stopPropagation();
              onAcceptAction([id]);
            }}
          >
            Accept
          </ButtonV3>
          <ButtonV3
            className={styles.rejectAction}
            startIcon="close"
            color="secondary"
            onClick={(e) => {
              e?.preventDefault();
              e?.stopPropagation();
              onDeclineAction([id]);
            }}
          >
            Decline
          </ButtonV3>
        </div>
      </>
    );
  };

  const getCards = useMemo(() => {
    const communityEventCards = events.map(
      (event: CommunityEventAttributes) => {
        const eventData = parseResponse<{
          icon_url: string;
          start_date: string;
        }>(event.touchpointable);

        const company = parseResponse<{ name: string }>(event.creatable_for);

        const opportunity = {
          id: event?.id,
          title: event?.title,
          start_date: eventData?.start_date
            ? format(new Date(eventData?.start_date || ''), 'MMM d, h:mma')
            : null,
          url: `${window.location.origin}${getOpportunityPublicPagePathname(
            event?.touchpointable_type,
            event?.slug
          )}`,
          avatar_urls: event?.registered_applications_avatar.map(
            (avatar: { avatar_url: string | null }) => avatar?.avatar_url
          ),
          applications_count: event?.applications,
          event_icon:
            eventData?.icon_url ||
            `${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/images/cover-image.png`,
          company_name: company?.name,
          isExpired: event?.state === TouchpointState.EXPIRED,
          application_deadline: event?.application_deadline,
          cities: event?.cities,
        } as CommunityEventCardDataProps;

        return <CommunityEventCard {...opportunity} key={event?.id} />;
      }
    );
    return <>{communityEventCards}</>;
  }, [events]);

  const PreferenceDetail: React.FC<PreferenceDetailProps> = ({
    label,
    values,
  }) => (
    <div className={styles.preferencesBlock}>
      <p className={styles.label}>{label}</p>
      <div className={styles.values}>
        {values.map((ele, index) => (
          <span key={index}>{ele}</span>
        ))}
      </div>
    </div>
  );

  const TalentPreferencesInfo: React.FC = () => {
    return (
      <div className={styles.preferences}>
        {Object.entries(TalentPreferences).map(([key, value]) =>
          value && value.length ? (
            <PreferenceDetail key={key} label={key} values={value} />
          ) : null
        )}
      </div>
    );
  };

  return (
    <div className={styles.CandidateApplication}>
      <div className={cn(styles.candidateData, { [styles.noActionBar]: true })}>
        {profileInfo()}
        {basicInfo()}
        {candidateEvents()}
        <TalentPreferencesInfo />
        {status === CommunityTypes.SUBSCRIPTION_REQUESTS && footer()}
      </div>
    </div>
  );
};

CommunityApplication.displayName = 'CommunityApplication';
